import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { IUsersCriteria, SortDirection } from '@e-origin/shared';

import { apiClient } from '@utils';

export const defaultUsersCriteria: IUsersCriteria = {
  filters: { searchQuery: '' },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'email', direction: SortDirection.ASC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): IUsersCriteria => {
  const filters: IUsersCriteria['filters'] = {};
  filters.searchQuery = searchParams.get('searchQuery') || '';
  filters.filterByDeclarants = searchParams.get('declarants')?.split(',')?.filter(Boolean) || [];

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'email';
  const sortDir = (searchParams.get('sortDir') || SortDirection.ASC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: IUsersCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  if (criteria.filters.filterByDeclarants) {
    searchParams.set('declarants', criteria.filters.filterByDeclarants.join(','));
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialUserCriteria = (searchParams: URLSearchParams): IUsersCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultUsersCriteria;
};

export const useUserCriteriaUrlSync = (criteria?: IUsersCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useAdminUsers = (criteria: IUsersCriteria = defaultUsersCriteria) => {
  const result = apiClient.admin.getUsers.useQuery(['admin-users-list', criteria], { query: { criteria } });
  return { ...result, data: result.data?.body };
};
